import { Button, Flex, FormControl, FormErrorMessage, Image, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Spacer, useDisclosure, Box } from "@chakra-ui/react";
import { useField } from "formik";
import { FiCamera, FiCheck, FiX, FiRotateCw } from "react-icons/fi";
import { useState, useEffect, useRef } from "react";
import config from "../utils/config";

interface IProps {
  label: string;
  name: string;
  mb?: string;
  large?: boolean;
}

interface Event<T = EventTarget> {
  target: T;
}

// Função para ler orientação EXIF (versão simples e segura)
const getOrientation = (file: File): Promise<number> => {
  return new Promise((resolve) => {
    // Se houver qualquer erro, simplesmente retorna -1 (sem rotação)
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer;
        if (!buffer || buffer.byteLength < 20) {
          resolve(-1);
          return;
        }
        
        const view = new DataView(buffer);
        const length = Math.min(view.byteLength, 65536); // Limitar a 64KB
        
        // Verificar se é JPEG
        if (view.getUint16(0, false) !== 0xFFD8) {
          resolve(-1);
          return;
        }
        
        // Procurar EXIF de forma simples
        for (let i = 2; i < length - 8; i++) {
          try {
            if (view.getUint16(i, false) === 0xFFE1) {
              if (i + 8 >= length) break;
              if (view.getUint32(i + 4, false) === 0x45786966) { // "Exif"
                const little = view.getUint16(i + 10, false) === 0x4949;
                const base = i + 12;
                
                if (base + 6 >= length) break;
                const ifdOffset = view.getUint32(base + 4, little);
                const ifdBase = base + ifdOffset;
                
                if (ifdBase + 2 >= length) break;
                const tagCount = view.getUint16(ifdBase, little);
                
                for (let j = 0; j < tagCount && (ifdBase + 2 + (j + 1) * 12) < length; j++) {
                  const tagPos = ifdBase + 2 + (j * 12);
                  if (tagPos + 2 >= length) break;
                  
                  if (view.getUint16(tagPos, little) === 0x0112) {
                    if (tagPos + 10 >= length) break;
                    const orientation = view.getUint16(tagPos + 8, little);
                    resolve(orientation);
                    return;
                  }
                }
                break;
              }
            }
          } catch {
            // Continuar procurando
            continue;
          }
        }
        
        resolve(-1);
      } catch {
        resolve(-1);
      }
    };
    reader.onerror = () => resolve(-1);
    reader.readAsArrayBuffer(file.slice(0, 65536));
  });
};

// Função para rotacionar imagem baseada na orientação EXIF
const rotateImage = (img: HTMLImageElement, orientation: number): { width: number; height: number; draw: (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => void } => {
  const width = img.width;
  const height = img.height;
  let canvasWidth = width;
  let canvasHeight = height;

  const draw = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Ajustar tamanho do canvas baseado na orientação
    switch (orientation) {
      case 5:
      case 6:
      case 7:
      case 8:
        canvasWidth = height;
        canvasHeight = width;
        break;
      default:
        canvasWidth = width;
        canvasHeight = height;
    }

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Mover para o centro
    ctx.translate(canvasWidth / 2, canvasHeight / 2);

    // Aplicar rotação baseada na orientação EXIF
    switch (orientation) {
      case 2:
        ctx.scale(-1, 1);
        break;
      case 3:
        ctx.rotate(Math.PI);
        break;
      case 4:
        ctx.scale(1, -1);
        break;
      case 5:
        ctx.rotate(Math.PI / 2);
        ctx.scale(1, -1);
        break;
      case 6:
        ctx.rotate(Math.PI / 2);
        break;
      case 7:
        ctx.rotate(-Math.PI / 2);
        ctx.scale(1, -1);
        break;
      case 8:
        ctx.rotate(-Math.PI / 2);
        break;
    }

    ctx.drawImage(img, -width / 2, -height / 2, width, height);
    ctx.restore();
  };

  return { width: canvasWidth, height: canvasHeight, draw };
};

const InputImage = ({ label, mb, large = false, ...props }: IProps) => {
  const [, { error, touched, value }, { setValue }] = useField(props);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [tempProcessedImage, setTempProcessedImage] = useState<string | null>(null);
  const { isOpen: isPreviewModalOpen, onOpen: onPreviewModalOpen, onClose: onPreviewModalClose } = useDisclosure();
  const { isOpen: isViewModalOpen, onOpen: onViewModalOpen, onClose: onViewModalClose } = useDisclosure();
  const storageKey = useRef(`inputImage_${props.name}_${Math.random().toString(36).substr(2, 9)}`);
  const hasRestoredRef = useRef(false);

  const randomId = useRef(Math.random().toString(36).slice(2)).current;

  // Função para salvar estado no sessionStorage
  const saveStateToStorage = (fileData: string, rot: number) => {
    try {
      sessionStorage.setItem(storageKey.current, JSON.stringify({
        fileData,
        rotation: rot,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.warn('Não foi possível salvar no sessionStorage:', e);
    }
  };


  // Limpar storage quando confirmar ou cancelar
  const clearStorage = () => {
    try {
      sessionStorage.removeItem(storageKey.current);
    } catch (e) {
      // Ignorar erros
    }
  };

  // Detectar quando a página volta ao foco (após voltar da câmera)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Pequeno delay para garantir que o input foi atualizado
        setTimeout(() => {
          // Verificar se há um arquivo pendente no input
          const input = document.getElementById(`image-field${randomId}`) as HTMLInputElement;
          if (input?.files && input.files.length > 0) {
            // Se o input ainda tem arquivo, processar diretamente via evento
            // O evento onChange será disparado automaticamente
            const event = new Event('change', { bubbles: true });
            input.dispatchEvent(event);
          } else {
            // Tentar restaurar do storage
            const saved = sessionStorage.getItem(storageKey.current);
            if (saved && !hasRestoredRef.current) {
              try {
                hasRestoredRef.current = true; // Marcar como restaurado
                const { fileData } = JSON.parse(saved);
                
                // Converter base64 de volta para File
                fetch(fileData).then(response => response.blob()).then((blob) => {
                  const file = new File([blob], 'restored-image.jpg', { type: 'image/jpeg' });
                  
                  // Simular evento de input - o ajustImage vai verificar o storage para rotação
                  const dataTransfer = new DataTransfer();
                  dataTransfer.items.add(file);
                  if (input) {
                    input.files = dataTransfer.files;
                    const changeEvent = new Event('change', { bubbles: true });
                    input.dispatchEvent(changeEvent);
                  }
                });
              } catch (e) {
                console.warn('Erro ao restaurar:', e);
                sessionStorage.removeItem(storageKey.current);
              }
            }
          }
        }, 100);
      }
    };

    const handlePageShow = (e: PageTransitionEvent) => {
      // Se a página foi restaurada do cache (back/forward)
      if (e.persisted) {
        hasRestoredRef.current = false; // Permitir nova restauração
        const saved = sessionStorage.getItem(storageKey.current);
        if (saved) {
          try {
            const { fileData } = JSON.parse(saved);
            fetch(fileData).then(response => response.blob()).then(blob => {
              const file = new File([blob], 'restored-image.jpg', { type: 'image/jpeg' });
              const input = document.getElementById(`image-field${randomId}`) as HTMLInputElement;
              if (input) {
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                input.files = dataTransfer.files;
                const changeEvent = new Event('change', { bubbles: true });
                input.dispatchEvent(changeEvent);
              }
            });
          } catch (e) {
            console.warn('Erro ao restaurar:', e);
            sessionStorage.removeItem(storageKey.current);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [randomId]); // Apenas randomId como dependência

  const open = () => {
    document.getElementById(`image-field${randomId}`)?.click();
  }

  const processImage = async (file: File, additionalRotation: number = 0, saveToForm: boolean = false): Promise<void> => {
    // 800 * 1.6 = 1280 px de lado maior. Antes era 900 (1440 px) com qualidade 0.95,
    // o que gerava fotos de 300 a 800 KB e estourava o localStorage com poucas notas pendentes.
    let maxWidth = 800;
    let maxHeight = 800;

    const orientation = await getOrientation(file);
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onloadend = () => {
        const imagePreview = reader.result as string;
        if (!saveToForm) {
          setPreviewImage(imagePreview);
        }

        let oImg = document.createElement("img");
        oImg.src = imagePreview;

        oImg.onload = async () => {
        // Criar canvas temporário para processar
        let tempCanvas = document.createElement("canvas");
        let tempCtx = tempCanvas.getContext("2d");
        if (!tempCtx) return;

        // Aplicar rotação EXIF primeiro
        let finalWidth = oImg.width;
        let finalHeight = oImg.height;
        
        if (orientation > 1 && orientation < 9) {
          const rotated = rotateImage(oImg, orientation);
          tempCanvas.width = rotated.width;
          tempCanvas.height = rotated.height;
          rotated.draw(tempCtx, tempCanvas);
          finalWidth = rotated.width;
          finalHeight = rotated.height;
        } else {
          tempCanvas.width = oImg.width;
          tempCanvas.height = oImg.height;
          tempCtx.drawImage(oImg, 0, 0);
        }

        // Aplicar rotação manual adicional
        if (additionalRotation !== 0) {
          const angle = (additionalRotation * Math.PI) / 180;
          const rotatedCanvas = document.createElement("canvas");
          const rotatedCtx = rotatedCanvas.getContext("2d");
          if (!rotatedCtx) return;

          // Calcular novas dimensões após rotação
          const absCos = Math.abs(Math.cos(angle));
          const absSin = Math.abs(Math.sin(angle));
          rotatedCanvas.width = finalWidth * absCos + finalHeight * absSin;
          rotatedCanvas.height = finalWidth * absSin + finalHeight * absCos;

          rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
          rotatedCtx.rotate(angle);
          rotatedCtx.drawImage(tempCanvas, -finalWidth / 2, -finalHeight / 2);

          tempCanvas = rotatedCanvas;
          finalWidth = rotatedCanvas.width;
          finalHeight = rotatedCanvas.height;
        }

        // Redimensionar se necessário
        let resizeMaxWidth = maxWidth * 1.6;
        let resizeMaxHeight = maxHeight * 1.6;

        let width = finalWidth;
        let height = finalHeight;

        if (width > resizeMaxWidth) {
          height = Math.round((height * resizeMaxWidth) / width);
          width = resizeMaxWidth;
        }

        if (height > resizeMaxHeight) {
          width = Math.round((width * resizeMaxHeight) / height);
          height = resizeMaxHeight;
        }

        // Canvas final
        let finalCanvas = document.createElement("canvas");
        finalCanvas.width = width;
        finalCanvas.height = height;
        const finalCtx = finalCanvas.getContext("2d");
        if (finalCtx) {
          finalCtx.drawImage(tempCanvas, 0, 0, width, height);
          const processedDataUrl = finalCanvas.toDataURL("image/jpeg", 0.8);
          
          if (saveToForm) {
            setValue(processedDataUrl);
          } else {
            setTempProcessedImage(processedDataUrl);
          }
          resolve();
        }

        // Limpar canvas temporário
        tempCanvas = document.createElement("canvas");
      };
      };
      reader.readAsDataURL(file);
    });
  };

  const ajustImage = (event: Event<HTMLInputElement>) => {
    let fileList = event.target.files;
    if (!fileList?.length) {
      return;
    }
    let file = fileList[0];

    if (!config.redimensionarFotoCanhoto) {
      // Sem opção de rotacionar: salva direto (só correção EXIF)
      processImage(file, 0, true).then(() => {
        const input = document.getElementById(`image-field${randomId}`) as HTMLInputElement;
        if (input) input.value = '';
      });
      return;
    }

    // Com opção de rotacionar: abre modal
    let savedRotation = 0;
    try {
      const saved = sessionStorage.getItem(storageKey.current);
      if (saved) {
        const parsed = JSON.parse(saved);
        savedRotation = parsed.rotation || 0;
      }
    } catch (e) {
      // Ignorar erros
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const fileData = e.target?.result as string;
      saveStateToStorage(fileData, savedRotation);
    };
    reader.readAsDataURL(file);

    setOriginalFile(file);
    setRotation(savedRotation);
    setTempProcessedImage(null);
    processImage(file, savedRotation, false).then(() => {
      onPreviewModalOpen();
    });
  };

  const handleRotate = () => {
    if (originalFile) {
      const newRotation = rotation + 90;
      setRotation(newRotation);
      
      // Atualizar rotação no storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileData = e.target?.result as string;
        saveStateToStorage(fileData, newRotation);
      };
      reader.readAsDataURL(originalFile);
      
      processImage(originalFile, newRotation, false);
    }
  };

  const confirmImage = () => {
    if (originalFile) {
      // Processar e salvar a imagem final
      processImage(originalFile, rotation, true).then(() => {
        // Fechar modal e limpar estados temporários
        onPreviewModalClose();
        setPreviewImage(null);
        setTempProcessedImage(null);
        setOriginalFile(null);
        setRotation(0);
        clearStorage(); // Limpar storage após confirmar
        // Resetar o input file
        const input = document.getElementById(`image-field${randomId}`) as HTMLInputElement;
        if (input) input.value = '';
      });
    }
  };

  const cancelImage = () => {
    onPreviewModalClose();
    setPreviewImage(null);
    setTempProcessedImage(null);
    setOriginalFile(null);
    setRotation(0);
    clearStorage(); // Limpar storage ao cancelar
    // Resetar o input file
    const input = document.getElementById(`image-field${randomId}`) as HTMLInputElement;
    if (input) input.value = '';
  };

  return (
    <Flex direction="column">
      <FormControl mb={mb ? mb : 0} isInvalid={touched && error ? true : false}>
        <Button 
          display={"flex"} 
          size={large ? 'md' : 'sm'} 
          fontSize={large ? '20px' : '16px'} 
          mr={1} 
          borderWidth={error && '3px'} 
          borderColor="red" 
          colorScheme="teal" 
          onClick={open}
        >
          <FiCamera /> &nbsp; {label}
        </Button>
        
        {/* Botões para ver/remover imagem já salva */}
        {value && (
          <Flex mx={"auto"} width={"full"} justifyContent={"center"} mt={2} gap={2}>
            <Button size="sm" colorScheme="teal" onClick={onViewModalOpen}>
              <FiCheck /> &nbsp; Ver foto
            </Button>
            <Button size="sm" colorScheme="red" variant="outline" onClick={() => {
              setValue('');
            }}>
              <FiX /> &nbsp; Remover
            </Button>
          </Flex>
        )}

        <input 
          type="file" 
          accept="image/*,text/plain"
          onChange={ajustImage} 
          style={{ display: 'none' }} 
          id={`image-field${randomId}`} 
        />
        {error && <FormErrorMessage mt={1}>{error}</FormErrorMessage>}
      </FormControl>
      
      {/* Modal de preview e confirmação - aparece ao tirar foto */}
      <Modal 
        closeOnOverlayClick={false} 
        isOpen={isPreviewModalOpen} 
        onClose={cancelImage}
        size="xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center" fontSize="lg">
            Ajuste a foto
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Flex direction="column" align="center" gap={4}>
              {/* Preview da imagem */}
              <Box 
                width="100%" 
                maxH="400px" 
                overflow="hidden" 
                borderRadius="md"
                borderWidth="2px"
                borderColor="gray.300"
                bg="gray.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={4}
              >
                <Image 
                  src={tempProcessedImage || previewImage || ''} 
                  alt='Preview' 
                  maxH="380px"
                  maxW="100%"
                  style={{ 
                    objectFit: 'contain'
                  }}
                />
              </Box>
              
              {/* Controle de rotação */}
              <Button
                leftIcon={<FiRotateCw />}
                onClick={handleRotate}
                colorScheme="blue"
                size="md"
                variant="outline"
              >
                Rotacionar foto
              </Button>

              {/* Botões de ação */}
              <Flex gap={3} width="full" mt={2}>
                <Button
                  flex={1}
                  colorScheme="red"
                  variant="outline"
                  onClick={cancelImage}
                  size="lg"
                >
                  <FiX /> &nbsp; Cancelar
                </Button>
                <Button
                  flex={1}
                  colorScheme="green"
                  onClick={confirmImage}
                  size="lg"
                >
                  <FiCheck /> &nbsp; Confirmar foto
                </Button>
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal para visualizar imagem já salva */}
      <Modal closeOnOverlayClick={false} isOpen={isViewModalOpen} onClose={onViewModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Pré visualização da imagem</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Image src={value} alt='imagem' />
            <Spacer marginY={5} />
            <Button colorScheme="teal" onClick={onViewModalClose}>Fechar</Button>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  )
}

export default InputImage;